import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';

import Logger from '@common/Logger.js';
import useFetching from '@hooks/Fetching/Fetching.js';
import useViewportSize from '@hooks/Viewport/Viewport.js';
import { addStandardPrice } from '@pages/EventDetails/Util.jsx';

import {
  createSessionService,
  calculateMenuItemsCount,
  formatSessionsForAgenda
} from './AgendaHelper.js';
import AgendaTimelineMenu from './AgendaTimelineMenu.jsx';
import SessionCard from './SessionCard.jsx';

const Agenda = ({ id, venues, minHeight }) => {
  const [itemsPerScreen, setItemsPerScreen] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [firstMenuItem, setFirstMenuItem] = useState(0);
  const [lastMenuItem, setLastMenuItem] = useState(itemsPerScreen);
  const [activeStep, setActiveStep] = useState(0);

  const [sessionsData, isLoading] = useFetching(createSessionService.bind(null, id));

  const location = venues?.length ? `${venues[0]?.city}, ${venues[0]?.country}` : '';
  const size = useViewportSize();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        if (sessionsData?.data?.content?.length) {
          const formattedSessionData = formatSessionsForAgenda(sessionsData?.data?.content);
          setMenuItems(formattedSessionData.menuDateItems);
          setActiveMenuItem(formattedSessionData.menuDateItems[0]);
          setSessions(formattedSessionData.sessions);
        }
      } catch (e) {
        Logger.error(e);
      }
    };
    fetchEvents();
  }, [sessionsData]);

  useEffect(() => {
    if (size && menuItems && menuItems.length) {
      const maxItems = calculateMenuItemsCount(size, menuItems);
      if (maxItems) {
        setItemsPerScreen(maxItems);
      }
    }
  }, [menuItems, size]);

  useEffect(() => {
    if (itemsPerScreen) {
      setLastMenuItem(itemsPerScreen);
    }
  }, [itemsPerScreen]);

  useEffect(() => {
    setActiveMenuItem(menuItems[activeStep]);
  }, [activeStep]);

  const updateMenuIndexes = (direction) => {
    const length = menuItems.length;

    switch (direction) {
      case 'forward':
        if (length !== lastMenuItem && (activeStep !== 0 || itemsPerScreen === 1)) {
          setFirstMenuItem(firstMenuItem + 1);
          setLastMenuItem(lastMenuItem + 1);
        }
        if (activeStep !== length - 1) {
          setActiveStep((prevStep) => prevStep + 1);
        }
        break;
      case 'backward':
        if (firstMenuItem !== 0 && activeStep !== length - 1) {
          setFirstMenuItem(firstMenuItem - 1);
          setLastMenuItem(lastMenuItem - 1);
        }
        if (activeStep !== 0) {
          setActiveStep((prevStep) => prevStep - 1);
        }
        break;
      case 'first':
        if (activeStep !== 0) {
          setFirstMenuItem(0);
          setLastMenuItem(itemsPerScreen);
          setActiveStep(0);
        }
        break;
      case 'last':
        if (activeStep !== length) {
          setFirstMenuItem(length - itemsPerScreen);
          setLastMenuItem(length);
          setActiveStep(length - 1);
        }
        break;
      default:
        setFirstMenuItem(0);
        setLastMenuItem(itemsPerScreen);
        break;
    }
  };

  const selectedDateMenuIndex =
    menuItems && menuItems.length && activeMenuItem
      ? menuItems.findIndex((element) => element === activeMenuItem)
      : null;

  const getSessions = () => {
    if ((selectedDateMenuIndex || selectedDateMenuIndex === 0) && sessions && sessions.length) {
      return sessions[selectedDateMenuIndex].map((item, index) => (
        <SessionCard key={index} session={addStandardPrice({ ...item, location })} />
      ));
    }
  };

  return (
    <>
      <Container data-test-id={'agendaContainer'} sx={{ marginTop: { xs: 0, sm: '-1.1rem' } }}>
        {isLoading && (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            sx={{ minHeight: minHeight }}
          >
            <CircularProgress />
          </Box>
        )}

        {menuItems && menuItems.length !== 0 && (
          <AgendaTimelineMenu
            menuItems={menuItems}
            firstMenuItem={firstMenuItem}
            lastMenuItem={lastMenuItem}
            itemsPerScreen={itemsPerScreen}
            activeItem={activeMenuItem}
            setActiveItem={setActiveMenuItem}
            setActiveStep={setActiveStep}
            activeStep={activeStep}
            updateMenuIndexes={updateMenuIndexes}
          />
        )}

        {!!sessions?.length && getSessions()}
      </Container>
    </>
  );
};

export default Agenda;
